import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Calendar, User, ArrowRight, Plus, Trash2, Edit } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const defaultBlogPosts = [
  {
    id: "1",
    title: "5 Tax Deductions You Might Be Missing",
    excerpt: "Many taxpayers overlook valuable deductions that could save them hundreds or even thousands of dollars. Learn about commonly missed deductions and how to claim them.",
    date: "January 15, 2026",
    author: "WMJ Enterprise Services",
    category: "Tax Tips",
  },
  {
    id: "2",
    title: "Self-Employed? Here's What You Need to Know About Quarterly Taxes",
    excerpt: "If you're self-employed or a freelancer, understanding quarterly estimated taxes is crucial to avoid penalties. This guide breaks down everything you need to know.",
    date: "January 10, 2026",
    author: "WMJ Enterprise Services",
    category: "Self-Employment",
  },
  {
    id: "3",
    title: "How to Organize Your Tax Documents Before Filing",
    excerpt: "Getting organized before tax season can save you time, stress, and money. Follow our checklist to ensure you have everything ready for a smooth filing process.",
    date: "January 5, 2026",
    author: "WMJ Enterprise Services",
    category: "Tax Preparation",
  },
  {
    id: "4",
    title: "Understanding Your Credit Score: A Beginner's Guide",
    excerpt: "Your credit score affects everything from loan rates to rental applications. Learn what factors influence your score and how to improve it over time.",
    date: "December 28, 2025",
    author: "WMJ Enterprise Services",
    category: "Credit Education",
  },
  {
    id: "5",
    title: "Tax Changes for 2026: What You Need to Know",
    excerpt: "Stay informed about the latest tax law changes that could affect your return this year. We break down the key updates and what they mean for you.",
    date: "December 20, 2025",
    author: "WMJ Enterprise Services",
    category: "Tax Updates",
  },
];

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState(defaultBlogPosts);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showReadModal, setShowReadModal] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    category: "Tax Tips",
    author: "WMJ Enterprise Services"
  });

  useEffect(() => {
    // Check if admin is logged in
    const adminAuth = sessionStorage.getItem('adminAuth');
    setIsAdmin(!!adminAuth);

    // Fetch blog posts from API
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/api/blog`);
        if (response.data && response.data.length > 0) {
          setBlogPosts(response.data);
        }
      } catch (error) {
        // Use default posts if API fails
        console.log("Using default blog posts");
      }
    };
    fetchPosts();
  }, []);

  // Shuffle array helper function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Show only 6 posts - if admin, show first 6; otherwise shuffle and show 6
  const displayedPosts = isAdmin 
    ? blogPosts.slice(0, 6) 
    : shuffleArray(blogPosts).slice(0, 6);

  const getAuthConfig = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth) {
      return { headers: { Authorization: `Basic ${auth}` } };
    }
    return {};
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.excerpt) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingPost) {
        await axios.put(`${API}/api/admin/blog/${editingPost.id}`, formData, getAuthConfig());
        toast.success("Blog post updated!");
      } else {
        await axios.post(`${API}/api/admin/blog`, formData, getAuthConfig());
        toast.success("Blog post added!");
      }
      
      // Refresh posts
      const response = await axios.get(`${API}/api/blog`);
      if (response.data && response.data.length > 0) {
        setBlogPosts(response.data);
      }
      
      setShowAddModal(false);
      setEditingPost(null);
      setFormData({ title: "", excerpt: "", category: "Tax Tips", author: "WMJ Enterprise Services" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save blog post");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this blog post?")) return;
    
    try {
      await axios.delete(`${API}/api/admin/blog/${postId}`, getAuthConfig());
      setBlogPosts(blogPosts.filter(p => p.id !== postId));
      toast.success("Blog post deleted!");
    } catch (error) {
      toast.error("Failed to delete blog post");
    }
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      category: post.category,
      author: post.author
    });
    setShowAddModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-page">
      <Header />
      
      <main className="pt-[75px] md:pt-[90px] pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-navy mb-4">
              Tax Tips & Financial Insights
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Stay informed with the latest tax strategies, financial advice, and industry updates from our team of experts.
            </p>
            {isAdmin && (
              <Button
                onClick={() => {
                  setEditingPost(null);
                  setFormData({ title: "", excerpt: "", category: "Tax Tips", author: "WMJ Enterprise Services" });
                  setShowAddModal(true);
                }}
                className="mt-6 bg-navy hover:bg-navy-light text-white"
                data-testid="add-blog-btn"
              >
                <Plus size={18} className="mr-2" />
                Add Blog Post
              </Button>
            )}
          </div>

          {/* Blog Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedPosts.map((post) => (
              <Card 
                key={post.id} 
                className="border-0 shadow-card hover:shadow-lg transition-shadow duration-300 overflow-hidden group relative cursor-pointer"
                onClick={() => {
                  setSelectedPost(post);
                  setShowReadModal(true);
                }}
              >
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(post);
                      }}
                      className="p-1.5 bg-white rounded-full shadow hover:bg-slate-100"
                    >
                      <Edit size={14} className="text-navy" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePost(post.id);
                      }}
                      className="p-1.5 bg-white rounded-full shadow hover:bg-red-50"
                    >
                      <Trash2 size={14} className="text-red-500" />
                    </button>
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium bg-gold/20 text-gold px-2 py-1 rounded">
                      {post.category}
                    </span>
                  </div>
                  
                  <h2 className="font-heading font-bold text-xl text-navy mb-3 group-hover:text-gold transition-colors">
                    {post.title}
                  </h2>
                  
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-slate-500 pt-4 border-t">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {post.date}
                      </span>
                    </div>
                    <span className="flex items-center gap-1 text-navy font-medium group-hover:text-gold transition-colors cursor-pointer">
                      Read More <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center bg-navy rounded-2xl p-8 md:p-12">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              Let our experienced team handle your tax preparation so you can focus on what matters most.
            </p>
            <Link
              to="/intake"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-3 rounded-lg transition-colors"
              data-testid="blog-cta"
            >
              Start My Tax Intake
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>

      {/* Add/Edit Blog Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-navy">
              {editingPost ? "Edit Blog Post" : "Add New Blog Post"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePost} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Title *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Blog post title"
                required
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="Tax Tips">Tax Tips</option>
                <option value="Tax Preparation">Tax Preparation</option>
                <option value="Self-Employment">Self-Employment</option>
                <option value="Credit Education">Credit Education</option>
                <option value="Tax Updates">Tax Updates</option>
                <option value="Business">Business</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Excerpt *</label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Brief description of the blog post..."
                required
                rows={4}
                className="mt-1"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-navy hover:bg-navy-light text-white"
              >
                {editingPost ? "Update Post" : "Add Post"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Read Blog Post Modal */}
      <Dialog open={showReadModal} onOpenChange={setShowReadModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-navy">
              {selectedPost?.title}
            </DialogTitle>
            <div className="flex items-center gap-4 text-sm text-slate-600 mt-2">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {selectedPost?.date}
              </span>
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {selectedPost?.author}
              </span>
              <span className="text-xs font-medium bg-gold/20 text-gold px-2 py-1 rounded">
                {selectedPost?.category}
              </span>
            </div>
          </DialogHeader>
          <div className="mt-6">
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">
              {selectedPost?.excerpt}
            </p>
            {selectedPost?.content && (
              <div className="mt-6 text-slate-700 leading-relaxed whitespace-pre-line">
                {selectedPost.content}
              </div>
            )}
          </div>
          <div className="mt-8 pt-6 border-t">
            <Button
              onClick={() => setShowReadModal(false)}
              className="w-full bg-navy hover:bg-navy-light text-white"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Blog;
